import { Form, formProviderHOC } from '@sravni/cosago-react-library/lib/components';
import { useControlledNavigationSequenceControl } from '@sravni/cosago-react-library/lib/hooks';
import type { FormComponentProps } from '@sravni/cosago-react-library/lib/types';
import { Icon, Space } from '@sravni/react-design-system';
import { Heading, Text } from '@sravni/react-design-system/lib/Typography';
import { Tip } from '@sravni/react-icons';
import { useIsMobile } from '@sravni/react-utils';
import type { FC } from 'react';

import type { TCommonAdditionalFormProps } from 'shared/types';
import { AnketaAlert } from 'shared/ui/AnketaAlert';
import { FormWithControlledNavigation } from 'shared/ui/FormWithControlledNavigation';

import type { TPledgeAutoFields } from 'entities/pledgeAutoForm';
import { usePledgeAutoFieldsControl } from 'entities/pledgeAutoForm/lib/usePledgeAutoFieldsControl';

import { CarInfoLoadingStatus } from 'features/PledgeAutoForm/fields';
import { FormFieldsNavigationSequence } from 'features/PledgeAutoForm/PledgeAutoForm.config';
import { PledgeAutoFormTexts } from 'features/PledgeAutoForm/PledgeAutoForm.texts';

import { useUpdateCarNumberField } from '../lib/useUpdateCarNumberField';

type Props = FormComponentProps<TPledgeAutoFields, TCommonAdditionalFormProps>;

const PledgeAutoFormView: FC<Props> = ({ onSubmit, NAMES, submitButtonText, FieldConstructor, isLoading }) => {
  const controls = useControlledNavigationSequenceControl({ fields: FormFieldsNavigationSequence });
  const { runUpdateCarNumberOnBlur } = useUpdateCarNumberField(NAMES.carNumber);
  const isMobile = useIsMobile();

  usePledgeAutoFieldsControl();

  return (
    <FormWithControlledNavigation
      onSubmit={onSubmit}
      controlElement={FieldConstructor}
      isDialog={isMobile}
      isLoading={isLoading}
      submitButtonText={submitButtonText}
      navigationControls={controls}
      isSubmitDisabled={isLoading}
    >
      <Form.Block>
        <Form.Row colWidths={[12]}>
          <AnketaAlert
            color="blue"
            variant="secondary"
            subtitle={PledgeAutoFormTexts.alertText}
            icon={
              <Icon
                size={20}
                icon={<Tip />}
              />
            }
          />
        </Form.Row>
        <Form.Row colWidths={[12]}>
          <Space
            direction="vertical"
            size={16}
          >
            <Heading level={4}>Автоматическая подгрузка данных</Heading>
            <Text size={14}>Если автомобиль поставлен на учёт, введите гос. номер и мы подгрузим все данные</Text>
          </Space>
        </Form.Row>
        <Form.Row colWidths={[5, 7]}>
          <FieldConstructor
            type={NAMES.carNumber}
            onEnterPressed={runUpdateCarNumberOnBlur}
          />
          <CarInfoLoadingStatus />
        </Form.Row>
        <Form.Row colWidths={[12]}>
          <Heading level={4}>Автомобиль</Heading>
        </Form.Row>
        <Form.Row colWidths={[5, 5]}>
          <FieldConstructor type={NAMES.carBrand} />
          <FieldConstructor type={NAMES.carModel} />
        </Form.Row>

        <Form.Row colWidths={[5, 5]}>
          <FieldConstructor type={NAMES.carManufactureYear} />
          <FieldConstructor type={NAMES.enginePower} />
        </Form.Row>
        <Form.Row colWidths={[5]}>
          <FieldConstructor type={NAMES.isOwner} />
        </Form.Row>
      </Form.Block>
    </FormWithControlledNavigation>
  );
};

export const PledgeAutoFormViewHoc = formProviderHOC<TPledgeAutoFields, TCommonAdditionalFormProps>(PledgeAutoFormView);
